import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  CircularProgress,
  FormControlLabel,
  Switch,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import BusinessSettingsContext from "../Context/Settings/BusinessSettingsContext";
import {
  getLists,
  createCampaign,
  prepareCampaign,
  waitForCampaignReady,
  startCampaign,
  ContactList,
  TemplateComponent,
  FollowUpAudience,
  FollowUpDelayUnit,
  CampaignBatchIntervalUnit,
  TemplateVariableMapping,
} from "../../services/campaign.service";
import useAuthUser from "../hooks/useAuthUser";
// ✅ reuse existing templates api
import { getAllTemplates } from "../../services/settings.service";
import { WhatsAppTemplate } from "../../models/whatsapp.model";
import {
  getTemplateHeaderType,
  templateHasMediaHeader,
} from "../../utilities/whatsappTemplate.utility";
import TemplateVariableMapper, { componentsFromVariableMappings } from "./TemplateVariableMapper";

import { DashboardFormSection } from "../dashboard/DashboardPage";
import { dashboardLayout } from "../dashboard/layoutTokens";

type Props = { businessId: string; onCreated?: (id: string) => void };

type FollowUpDraft = {
  delayAmount: number;
  delayUnit: FollowUpDelayUnit;
  audience: FollowUpAudience;
  templateName: string;
  variableMappings: TemplateVariableMapping[];
};

type BatchScheduleMode = "INTERVAL" | "CUSTOM_DATES" | "PARENT_SCHEDULE";
type DateTimeDraft = { date: string; time: string };
type RecipientRangeDraft = { start: number; end: number };

const newFollowUpDraft = (): FollowUpDraft => ({
  delayAmount: 2,
  delayUnit: "DAYS",
  audience: "ALL",
  templateName: "",
  variableMappings: [],
});

export default function CampaignCreate({ businessId, onCreated }: Props) {
  const { settings } = useContext(BusinessSettingsContext);

  // uuid is actually businessId in your campaign world
  const uuid = businessId || settings?.businessId;

  const [lists, setLists] = useState<ContactList[]>([]);
  const [approvedTemplates, setApprovedTemplates] = useState<
    WhatsAppTemplate[]
  >([]);

  const [name, setName] = useState<string>("");
  const [listIds, setListIds] = useState<string[]>([]);
  const [recipientRangeEnabled, setRecipientRangeEnabled] = useState<boolean>(false);
  const [recipientRanges, setRecipientRanges] = useState<Record<string, RecipientRangeDraft>>({});

  //  selected template
  const [selectedTplName, setSelectedTplName] = useState<string>("");
  const selectedTemplate = useMemo(
    () => approvedTemplates.find((t) => t.name === selectedTplName) || null,
    [approvedTemplates, selectedTplName],
  );

  const [lang, setLang] = useState<string>("en_US");
  const [bodyVars, setBodyVars] = useState<TemplateVariableMapping[]>([]);
  const [scheduleEnabled, setScheduleEnabled] = useState<boolean>(false);
  const [scheduledDateValue, setScheduledDateValue] = useState<string>("");
  const [scheduledTimeValue, setScheduledTimeValue] = useState<string>("");
  const [batchingEnabled, setBatchingEnabled] = useState<boolean>(false);
  const [batchSize, setBatchSize] = useState<number>(1000);
  const [batchIntervalAmount, setBatchIntervalAmount] = useState<number>(1);
  const [batchIntervalUnit, setBatchIntervalUnit] = useState<CampaignBatchIntervalUnit>("DAYS");
  const [batchScheduleMode, setBatchScheduleMode] = useState<BatchScheduleMode>("INTERVAL");
  const [customBatchDates, setCustomBatchDates] = useState<DateTimeDraft[]>([]);
  const [followUpsEnabled, setFollowUpsEnabled] = useState<boolean>(false);
  const [followUpSteps, setFollowUpSteps] = useState<FollowUpDraft[]>([
    newFollowUpDraft(),
  ]);

  const [err, setErr] = useState<string>("");
  const [info, setInfo] = useState<string>("");

  const [busy, setBusy] = useState<boolean>(false);
  const [loadingLists, setLoadingLists] = useState<boolean>(false);
  const [loadingTemplates, setLoadingTemplates] = useState<boolean>(false);

  const [listsError, setListsError] = useState("");
  const [templatesError, setTemplatesError] = useState("");

  const loadLists = useCallback(async () => {
    if (!uuid) return;
    setLoadingLists(true);
    setListsError("");
    try { setLists(await getLists(uuid)); }
    catch { setListsError("We could not load your contact lists. Try again."); }
    finally { setLoadingLists(false); }
  }, [uuid]);
  useEffect(() => { void loadLists(); }, [loadLists]);

  const { user } = useAuthUser();
  const loadTemplates = useCallback(async () => {
    if (!user?.uid) return;
    setLoadingTemplates(true);
    setTemplatesError("");
    try {
      const templates = await getAllTemplates(user.uid);
      const approved = (templates || []).filter(template => template.status === "APPROVED");
      setApprovedTemplates(approved);
      if (approved.length) {
        setSelectedTplName(approved[0].name);
        setLang(approved[0].language || "en_US");
      }
    } catch { setTemplatesError("We could not load your approved templates. Try again."); }
    finally { setLoadingTemplates(false); }
  }, [user?.uid]);
  useEffect(() => { void loadTemplates(); }, [loadTemplates]);

  const selectedHeaderType = getTemplateHeaderType(selectedTemplate);
  const requiresMediaHeader = templateHasMediaHeader(selectedTemplate);
  const availableColumns = useMemo(() => {
    const selectedLists = lists.filter((list) => listIds.includes(list._id));
    return Array.from(
      new Set(["name", "phone", ...selectedLists.flatMap((list) => list.columns || [])]),
    );
  }, [lists, listIds]);
  const estimatedRecipientCount = useMemo(
    () => lists.filter((list) => listIds.includes(list._id)).reduce((total, list) => {
      const memberCount = Number(list.memberCount || 0);
      if (!recipientRangeEnabled) return total + memberCount;
      const range = recipientRanges[list._id];
      const start = Math.max(1, range?.start || 1);
      const end = Math.min(memberCount, range?.end || memberCount);
      return total + Math.max(0, end - start + 1);
    }, 0),
    [lists, listIds, recipientRangeEnabled, recipientRanges],
  );
  const estimatedBatchCount = batchingEnabled && estimatedRecipientCount > 0
    ? Math.ceil(estimatedRecipientCount / Math.max(batchSize, 1))
    : 0;

  useEffect(() => {
    if (batchScheduleMode !== "CUSTOM_DATES") return;
    setCustomBatchDates((current) =>
      Array.from({ length: estimatedBatchCount }, (_, index) => current[index] || { date: "", time: "" }),
    );
  }, [batchScheduleMode, estimatedBatchCount]);

  const buildComponents = (): TemplateComponent[] => {
    return componentsFromVariableMappings(bodyVars);
  };

  const onCreate = async () => {
    if (!uuid) return;

    setErr("");
    setInfo("");
    setBusy(true);

    try {
      if (!selectedTemplate?.name) {
        setErr("Please select an approved template");
        return;
      }

      const scheduledStartInput = scheduledDateValue && scheduledTimeValue
        ? new Date(`${scheduledDateValue}T${scheduledTimeValue}`)
        : null;
      const usesCustomBatchDates = batchingEnabled && batchScheduleMode === "CUSTOM_DATES";
      if (scheduleEnabled && !usesCustomBatchDates && (!scheduledStartInput || Number.isNaN(scheduledStartInput.getTime()))) {
        setErr("Choose a valid campaign date and time");
        return;
      }
      if (scheduleEnabled && !usesCustomBatchDates && scheduledStartInput && scheduledStartInput.getTime() <= Date.now()) {
        setErr("Scheduled campaign time must be in the future");
        return;
      }

      const customScheduleDates = usesCustomBatchDates
        ? customBatchDates.slice(0, estimatedBatchCount).map((item) =>
            item.date && item.time ? new Date(`${item.date}T${item.time}`) : null,
          )
        : [];
      if (batchingEnabled && batchScheduleMode === "CUSTOM_DATES") {
        if (!estimatedBatchCount || customScheduleDates.some((date) => !date || Number.isNaN(date.getTime()))) {
          setErr("Choose a date and time for every staggered batch");
          return;
        }
        if (customScheduleDates.some((date) => date!.getTime() <= Date.now())) {
          setErr("Every staggered batch date must be in the future");
          return;
        }
        if (customScheduleDates.some((date, index) => index > 0 && date!.getTime() <= customScheduleDates[index - 1]!.getTime())) {
          setErr("Each staggered batch must be scheduled after the previous batch");
          return;
        }
      }
      const scheduledStart = usesCustomBatchDates
        ? customScheduleDates[0]
        : scheduleEnabled
          ? scheduledStartInput
          : null;

      if (followUpsEnabled) {
        for (let index = 0; index < followUpSteps.length; index += 1) {
          const step = followUpSteps[index];
          const followUpTemplate = approvedTemplates.find(
            (template) => template.name === step.templateName,
          );
          if (!followUpTemplate) {
            setErr(`Select an approved template for follow-up ${index + 1}`);
            return;
          }
          if (!Number.isInteger(step.delayAmount) || step.delayAmount < 1) {
            setErr(`Follow-up ${index + 1} needs a delay of at least 1`);
            return;
          }
        }
      }

      if (recipientRangeEnabled) {
        const invalidRange = lists.filter((list) => listIds.includes(list._id)).find((list) => {
          const memberCount = Number(list.memberCount || 0);
          const range = recipientRanges[list._id] || { start: 1, end: memberCount };
          return range.start < 1 || range.end < range.start || range.end > memberCount;
        });
        if (invalidRange) {
          setErr(`Choose a valid recipient range for ${invalidRange.name}`);
          return;
        }
      }

      const incompleteVariable = bodyVars.find(
        (variable) =>
          (variable.source === "COLUMN" && !variable.column) ||
          (variable.source === "STATIC" && !variable.value?.trim()),
      );
      if (incompleteVariable) {
        setErr(`Choose a contact column or fixed value for {{${incompleteVariable.parameterName}}}`);
        return;
      }

      for (let index = 0; index < followUpSteps.length; index += 1) {
        const incompleteFollowUpVariable = followUpSteps[index].variableMappings.find(
          (variable) =>
            (variable.source === "COLUMN" && !variable.column) ||
            (variable.source === "STATIC" && !variable.value?.trim()),
        );
        if (followUpsEnabled && incompleteFollowUpVariable) {
          setErr(`Choose a source for follow-up ${index + 1} variable {{${incompleteFollowUpVariable.parameterName}}}`);
          return;
        }
      }

      const followUpPayload = followUpsEnabled ? followUpSteps.map((step) => {
        const template = approvedTemplates.find(
          (candidate) => candidate.name === step.templateName,
        )!;
        const components = componentsFromVariableMappings(step.variableMappings);
        return {
          delayAmount: step.delayAmount,
          delayUnit: step.delayUnit,
          audience: step.audience,
          template: {
            name: template.name,
            language: template.language || "en_US",
            components,
            variableMappings: step.variableMappings,
          },
        };
      }) : [];

      const payload = {
        uuid,
        name,
        listIds,
        listRanges: recipientRangeEnabled
          ? lists.filter((list) => listIds.includes(list._id)).map((list) => ({
              listId: list._id,
              start: recipientRanges[list._id]?.start || 1,
              end: recipientRanges[list._id]?.end || Number(list.memberCount || 0),
            }))
          : [],
        template: {
          name: selectedTemplate.name, // ✅ no input box needed
          language: lang,
          components: buildComponents(),
          variableMappings: bodyVars,
        },
        scheduledAt: scheduledStart ? scheduledStart.toISOString() : null,
        followUps: {
          enabled: followUpsEnabled,
          steps: followUpsEnabled ? followUpPayload : [],
        },
        batching: {
          enabled: batchingEnabled,
          batchSize,
          intervalAmount: batchIntervalAmount,
          intervalUnit: batchIntervalUnit,
          scheduleMode: batchScheduleMode,
          customSchedule: customScheduleDates.filter(Boolean).map((date) => date!.toISOString()),
        },
      };

      const { id } = await createCampaign(payload);
      setInfo("Campaign created. Preparing recipients...");
      await prepareCampaign(uuid, id);
      await waitForCampaignReady(uuid, id);

      if (scheduledStart) {
        setInfo(`Campaign scheduled for ${scheduledStart.toLocaleString()}.`);
      } else {
        setInfo("Recipients prepared. Starting send...");
        await startCampaign(uuid, id);
      }

      onCreated?.(id);
      // nav(`/campaign/view/${id}`);
    } catch (e: any) {
      setErr(
        e?.response?.data?.detail ||
          e?.message ||
          "Failed to create/start campaign",
      );
    } finally {
      setBusy(false);
    }
  };

  if (!uuid) return <Alert severity="warning">Loading settings...</Alert>;

  return (
    <Box sx={{ maxWidth: dashboardLayout.formMaxWidth, minWidth: 0 }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, border: "1px solid", borderColor: "divider" }}>
        <Typography id="campaign-create-title" component="h2" variant="h5">Create campaign</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: .75 }}>Choose your message and audience, then configure how it is delivered.</Typography>

        {err && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {err}
          </Alert>
        )}
        {info && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {info}
          </Alert>
        )}

        <Stack spacing={3} sx={{ mt: 3 }}>
          <DashboardFormSection title="Message" description="Use an approved WhatsApp template and personalize its content.">
          <TextField
            label="Campaign name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />

          {/* ✅ Approved Template Select */}
          <FormControl fullWidth disabled={loadingTemplates}>
            <InputLabel id="tpl-select-label">
              Select Approved Template
            </InputLabel>
            <Select
              labelId="tpl-select-label"
              value={selectedTplName}
              label="Select Approved Template"
              onChange={(e) => {
                const tpl = e.target.value as string;
                setSelectedTplName(tpl);

                const found = approvedTemplates.find((t) => t.name === tpl);
                if (found?.language) setLang(found.language);
              }}
              input={<OutlinedInput label="Select Approved Template" />}
            >
              {approvedTemplates.map((t) => (
                <MenuItem key={t.name} value={t.name}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>

            {loadingTemplates && (
              <Typography variant="caption" sx={{ mt: 1, opacity: 0.8 }}>
                <CircularProgress size={14} sx={{ mr: 1 }} />
                Loading templates...
              </Typography>
            )}

            {!loadingTemplates && !templatesError && approvedTemplates.length === 0 && (
              <Typography variant="caption" sx={{ mt: 1, opacity: 0.8 }}>
                No approved templates found. Create and get one approved first.
              </Typography>
            )}
          </FormControl>

          <TextField
            label="Language"
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            fullWidth
          />

          {requiresMediaHeader && (
            <Alert severity="info">
              This template’s approved {selectedHeaderType.toLowerCase()} header
              will be included automatically.
            </Alert>
          )}

          {templatesError && <Alert severity="error" action={<Button color="inherit" onClick={loadTemplates} disabled={loadingTemplates}>Retry templates</Button>}>{templatesError}</Alert>}
          <TemplateVariableMapper
            template={selectedTemplate}
            availableColumns={availableColumns}
            value={bodyVars}
            onChange={setBodyVars}
            title="Personalize template variables"
          />
          </DashboardFormSection>
          <Divider />
          <DashboardFormSection title="Audience" description="Choose contact lists, with optional row limits for each list.">
          <FormControl fullWidth disabled={loadingLists}>
            <InputLabel id="list-select-label">Select Lists</InputLabel>
            <Select
              labelId="list-select-label"
              multiple
              value={listIds}
              onChange={(e) => {
                const value = e.target.value;
                setListIds(
                  typeof value === "string"
                    ? value.split(",")
                    : (value as string[]),
                );
              }}
              input={<OutlinedInput label="Select Lists" />}
            >
              {lists.map((l) => (
                <MenuItem key={l._id} value={l._id}>
                  {l.name}
                </MenuItem>
              ))}
            </Select>

            {loadingLists && (
              <Typography variant="caption" sx={{ mt: 1, opacity: 0.8 }}>
                <CircularProgress size={14} sx={{ mr: 1 }} />
                Loading lists...
              </Typography>
            )}
          </FormControl>

          {listsError && <Alert severity="error" action={<Button color="inherit" onClick={loadLists} disabled={loadingLists}>Retry lists</Button>}>{listsError}</Alert>}
          {!loadingLists && !listsError && lists.length === 0 && <Alert severity="info">Create a list in Lists &amp; contacts, then return here to select your audience.</Alert>}
          <FormControlLabel
            control={
              <Switch
                checked={recipientRangeEnabled}
                onChange={(event) => setRecipientRangeEnabled(event.target.checked)}
              />
            }
            label="Limit recipient range"
          />

          {recipientRangeEnabled && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Alert severity="info">
                  Row numbers are inclusive and follow each list's import order. For example, 1 to 100 includes exactly the first 100 contacts.
                </Alert>
                {lists.filter((list) => listIds.includes(list._id)).map((list) => {
                  const memberCount = Number(list.memberCount || 0);
                  const range = recipientRanges[list._id] || { start: 1, end: memberCount };
                  const selectedCount = Math.max(0, Math.min(memberCount, range.end) - Math.max(1, range.start) + 1);
                  return (
                    <Stack key={list._id} direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
                      <Box sx={{ minWidth: { md: 160 }, flex: 1 }}>
                        <Typography fontWeight={700}>{list.name}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                          {memberCount.toLocaleString()} contacts · {selectedCount.toLocaleString()} selected
                        </Typography>
                      </Box>
                      <TextField
                        type="number"
                        label="From row"
                        value={range.start}
                        onChange={(event) => setRecipientRanges((ranges) => ({
                          ...ranges,
                          [list._id]: { ...range, start: Number(event.target.value) },
                        }))}
                        inputProps={{ min: 1, max: memberCount }}
                        fullWidth
                      />
                      <TextField
                        type="number"
                        label="To row"
                        value={range.end}
                        onChange={(event) => setRecipientRanges((ranges) => ({
                          ...ranges,
                          [list._id]: { ...range, end: Number(event.target.value) },
                        }))}
                        inputProps={{ min: 1, max: memberCount }}
                        fullWidth
                      />
                    </Stack>
                  );
                })}
                {!listIds.length && <Alert severity="warning">Select a list first.</Alert>}
                <Typography variant="body2" fontWeight={700}>
                  Final campaign audience: {estimatedRecipientCount.toLocaleString()} contacts
                </Typography>
              </Stack>
            </Paper>
          )}

          </DashboardFormSection>
          <Divider />
          <DashboardFormSection title="Delivery" description="Send immediately, schedule for later or split delivery into batches.">
          <FormControlLabel
            control={
              <Switch
                checked={scheduleEnabled}
                onChange={(event) => setScheduleEnabled(event.target.checked)}
              />
            }
            label="Schedule for later"
          />

          {scheduleEnabled && !(batchingEnabled && batchScheduleMode === "CUSTOM_DATES") && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Campaign start date"
                type="date"
                value={scheduledDateValue}
                onChange={(event) => setScheduledDateValue(event.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
              />
              <TextField
                label="Campaign start time"
                type="time"
                value={scheduledTimeValue}
                onChange={(event) => setScheduledTimeValue(event.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
                helperText={`Local timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`}
                required
                fullWidth
              />
            </Stack>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={batchingEnabled}
                onChange={(event) => setBatchingEnabled(event.target.checked)}
              />
            }
            label="Enable staggered batches"
          />

          {batchingEnabled && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Alert severity="info">
                  Split this campaign into smaller delivery batches while keeping combined analytics under the main campaign.
                </Alert>
                <TextField
                  type="number"
                  label="Contacts per batch"
                  value={batchSize}
                  onChange={(event) => setBatchSize(Number(event.target.value))}
                  slotProps={{ htmlInput: { min: 1, max: 10000, step: 1 } }}
                  helperText={estimatedRecipientCount
                    ? `${estimatedRecipientCount.toLocaleString()} selected contacts will create ${estimatedBatchCount} batches.`
                    : "Example: 5,000 contacts with 1,000 per batch creates 5 batches."}
                  fullWidth
                />
                <ToggleButtonGroup
                  exclusive
                  value={batchScheduleMode}
                  onChange={(_, value: BatchScheduleMode | null) => {
                    if (!value) return;
                    setBatchScheduleMode(value);
                    if (value === "CUSTOM_DATES") setScheduleEnabled(false);
                  }}
                  fullWidth
                  aria-label="Batch schedule mode"
                  sx={{ flexWrap: "wrap", "& .MuiToggleButton-root": { flex: "1 1 140px", whiteSpace: "normal", textTransform: "none" } }}
                >
                  <ToggleButton value="INTERVAL">Use fixed interval</ToggleButton>
                  <ToggleButton value="CUSTOM_DATES">Choose exact dates</ToggleButton>
                  <ToggleButton value="PARENT_SCHEDULE">Use parent schedule</ToggleButton>
                </ToggleButtonGroup>

                {batchScheduleMode === "INTERVAL" ? (
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <TextField
                    type="number"
                    label="Time between batches"
                    value={batchIntervalAmount}
                    onChange={(event) => setBatchIntervalAmount(Number(event.target.value))}
                    slotProps={{ htmlInput: { min: 1, max: 365 } }}
                    fullWidth
                  />
                  <FormControl fullWidth>
                    <InputLabel>Interval unit</InputLabel>
                    <Select
                      value={batchIntervalUnit}
                      label="Interval unit"
                      onChange={(event) => setBatchIntervalUnit(event.target.value as CampaignBatchIntervalUnit)}
                    >
                      <MenuItem value="HOURS">Hours</MenuItem>
                      <MenuItem value="DAYS">Days</MenuItem>
                    </Select>
                  </FormControl>
                  </Stack>
                ) : batchScheduleMode === "CUSTOM_DATES" ? (
                  <Stack spacing={1.5}>
                    {!estimatedBatchCount && (
                      <Alert severity="warning">Select at least one list with contacts to create the batch schedule.</Alert>
                    )}
                    {customBatchDates.map((item, index) => (
                      <Stack key={index} direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <Typography fontWeight={700} sx={{ minWidth: 80, alignSelf: "center" }}>
                          Batch {index + 1}
                        </Typography>
                        <TextField
                          label={`Batch ${index + 1} date`}
                          type="date"
                          value={item.date}
                          onChange={(event) => setCustomBatchDates((dates) => dates.map((date, dateIndex) => dateIndex === index ? { ...date, date: event.target.value } : date))}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                        <TextField
                          label={`Batch ${index + 1} time`}
                          type="time"
                          value={item.time}
                          onChange={(event) => setCustomBatchDates((dates) => dates.map((date, dateIndex) => dateIndex === index ? { ...date, time: event.target.value } : date))}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 60 }}
                          fullWidth
                        />
                      </Stack>
                    ))}
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      All dates and times use {Intl.DateTimeFormat().resolvedOptions().timeZone}.
                    </Typography>
                  </Stack>
                ) : (
                  <Alert severity="info">
                    Every batch will use the parent campaign's date and time. If Schedule for later is off, all batches become available immediately and are sent sequentially.
                  </Alert>
                )}
              </Stack>
            </Paper>
          )}

          </DashboardFormSection>
          <Divider />
          <DashboardFormSection title="Follow-ups" description="Add follow-up messages when your campaign needs a sequence.">
          <FormControlLabel
            control={
              <Switch
                checked={followUpsEnabled}
                onChange={(event) => setFollowUpsEnabled(event.target.checked)}
              />
            }
            label="Enable campaign follow-ups"
          />

          {followUpsEnabled && (
            <Stack spacing={2}>
              <Alert severity="info">
                Add up to five follow-ups. Each delay starts after the previous
                campaign message. “Interested only” uses contacts currently in
                this campaign’s Interested funnel stage.
              </Alert>

              {followUpSteps.map((step, index) => {
                const stepTemplate = approvedTemplates.find(
                  (template) => template.name === step.templateName,
                );
                const stepNeedsMedia = templateHasMediaHeader(stepTemplate);
                const stepHeaderType = getTemplateHeaderType(stepTemplate);

                return (
                  <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography fontWeight={700}>
                          Follow-up {index + 1}
                        </Typography>
                        <Button
                          color="error"
                          size="small"
                          onClick={() =>
                            setFollowUpSteps((steps) =>
                              steps.filter((_, stepIndex) => stepIndex !== index),
                            )
                          }
                          disabled={followUpSteps.length === 1}
                        >
                          Remove
                        </Button>
                      </Stack>

                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <TextField
                          label="Delay after previous send"
                          type="number"
                          value={step.delayAmount}
                          onChange={(event) => {
                            const value = Number(event.target.value);
                            setFollowUpSteps((steps) =>
                              steps.map((item, stepIndex) =>
                                stepIndex === index
                                  ? { ...item, delayAmount: value }
                                  : item,
                              ),
                            );
                          }}
                          slotProps={{ htmlInput: { min: 1, max: 365 } }}
                          fullWidth
                        />
                        <FormControl fullWidth>
                          <InputLabel>Delay unit</InputLabel>
                          <Select
                            value={step.delayUnit}
                            label="Delay unit"
                            onChange={(event) =>
                              setFollowUpSteps((steps) =>
                                steps.map((item, stepIndex) =>
                                  stepIndex === index
                                    ? {
                                        ...item,
                                        delayUnit: event.target.value as FollowUpDelayUnit,
                                      }
                                    : item,
                                ),
                              )
                            }
                          >
                            <MenuItem value="HOURS">Hours</MenuItem>
                            <MenuItem value="DAYS">Days</MenuItem>
                          </Select>
                        </FormControl>
                      </Stack>

                      <FormControl fullWidth>
                        <InputLabel>Audience</InputLabel>
                        <Select
                          value={step.audience}
                          label="Audience"
                          onChange={(event) =>
                            setFollowUpSteps((steps) =>
                              steps.map((item, stepIndex) =>
                                stepIndex === index
                                  ? {
                                      ...item,
                                      audience: event.target.value as FollowUpAudience,
                                    }
                                  : item,
                              ),
                            )
                          }
                        >
                          <MenuItem value="ALL">All original recipients</MenuItem>
                          <MenuItem value="INTERESTED">Interested only</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl fullWidth>
                        <InputLabel>Approved follow-up template</InputLabel>
                        <Select
                          value={step.templateName}
                          label="Approved follow-up template"
                          onChange={(event) =>
                            setFollowUpSteps((steps) =>
                              steps.map((item, stepIndex) =>
                                stepIndex === index
                                    ? {
                                        ...item,
                                        templateName: event.target.value,
                                      }
                                  : item,
                              ),
                            )
                          }
                        >
                          {approvedTemplates.map((template) => (
                            <MenuItem key={template.name} value={template.name}>
                              {template.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <TemplateVariableMapper
                        template={stepTemplate}
                        availableColumns={availableColumns}
                        value={step.variableMappings}
                        onChange={(variableMappings) => setFollowUpSteps((steps) =>
                          steps.map((item, stepIndex) => stepIndex === index ? { ...item, variableMappings } : item),
                        )}
                        title={`Follow-up ${index + 1} variables`}
                      />

                      {stepNeedsMedia && (
                        <Alert severity="info">
                          The approved {stepHeaderType.toLowerCase()} header for this
                          template will be included automatically.
                        </Alert>
                      )}
                    </Stack>
                  </Paper>
                );
              })}

              <Button
                variant="outlined"
                onClick={() =>
                  setFollowUpSteps((steps) => [...steps, newFollowUpDraft()])
                }
                disabled={followUpSteps.length >= 5}
              >
                Add follow-up ({followUpSteps.length}/5)
              </Button>
            </Stack>
          )}



          </DashboardFormSection>
          <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 3 }}>
          <Button
            variant="contained"
            sx={{ width: { xs: "100%", sm: "auto" } }}
            onClick={onCreate}
            disabled={
              busy || loadingLists || loadingTemplates || Boolean(listsError) || Boolean(templatesError) ||
              !name.trim() ||
              !selectedTplName.trim() ||
              listIds.length === 0 ||
              approvedTemplates.length === 0 ||
              (scheduleEnabled && batchScheduleMode !== "CUSTOM_DATES" && (!scheduledDateValue || !scheduledTimeValue)) ||
              (batchingEnabled && (batchSize < 1 || batchSize > 10000 || batchIntervalAmount < 1)) ||
              (batchingEnabled && batchScheduleMode === "CUSTOM_DATES" && (
                !estimatedBatchCount || customBatchDates.some((item) => !item.date || !item.time)
              ))
            }
          >
            {busy
              ? scheduleEnabled || (batchingEnabled && batchScheduleMode === "CUSTOM_DATES")
                ? "Scheduling..."
                : "Starting..."
              : scheduleEnabled || (batchingEnabled && batchScheduleMode === "CUSTOM_DATES")
                ? "Create & Schedule"
                : "Create & Start"}
          </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
